import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';

import { lowerCase, snakeCase } from 'lodash';
import { Trash } from 'lucide-react';
import { InputGroup, InputGroupText, Select as SelectTW, ButtonIcon } from 'twyne-new.ui';

import InfoTip from '@Components/InfoTip';
import Select from '@Components/formsy/Select';
import { stateOptions } from '@Utils/index';

import RuleGroupItemWrapper from '../pure/rules/RuleGroup/Item';

import RuleItemDate from './RuleItemDate';
import RuleItemInput from './RuleItemInput';
import RuleItemValidator from './RuleItemValidator';
import { getDomainOptions, getEqualityOptions, getConsentDBAsOptions } from './options';

function RuleGroupItem({ data, condition, loadRuleKeys, origin, disabled, onDeleteRow, onChange, handleOpenValidatorModal }) {
  const [groupOpts, setGroupOpts] = useState([])

  useEffect(() => {
    loadRuleKeys().then(response => {
      const labeledGroups = separateGroupsByLabel(response);
      setGroupOpts(labeledGroups)
    })
  }, [loadRuleKeys])

  const handleChangeKeyInput = value => {
    if (!value) {
      return
    }

    const newData = Object.assign(data, { key: value.value, type: value.type });
    delete newData.value
    delete newData.equality
    if (value.type === 'select') {
      newData.options = value.options;
    }
    onChange(newData);
  }

  const handleChangeEqualityInput = value => {
    if (value) {
      const newData = Object.assign(data, { equality: value.value });

      if (['missing', 'not-missing'].includes(value.value)) {
        handleChangeValueInput('')
      }

      onChange(newData);
    }
  }

  const handleChangeValueInput = value => {
    const newData = Object.assign(data, { value });
    onChange(newData);
  }

  const separateGroupsByLabel = opts => {
    const extraOptions = []
    const tcpaOptions = opts.filter(opt => opt.group === 'TCPA')
    const currentSessionOptions = opts.filter(opt => opt.group === 'Current Session')
    const staticDBAsOptions = opts.filter(opt => opt.group === 'Static DBAs')

    if (tcpaOptions.length) {
      extraOptions.push({ value: 'group', label: '-- TCPA --', group: 'TCPA', disabled: true }, ...tcpaOptions)
    }

    if (currentSessionOptions.length) {
      extraOptions.push({ value: 'group', label: '-- Current Session --', group: 'Current Session', disabled: true }, ...currentSessionOptions)
    }

    if (staticDBAsOptions.length) {
      extraOptions.push({ value: 'group', label: '-- Static DBAs --', group: 'Static DBAs', disabled: true }, ...staticDBAsOptions)
    }

    return [
      { value: 'group', label: '-- Traffic --', group: 'Traffic', disabled: true },
      ...opts.filter(opt => opt.group === 'Traffic'),

      { value: 'group', label: '-- Verifications --', group: 'Verifications', disabled: true },
      ...opts.filter(opt => opt.group === 'Verifications'),

      { value: 'group', label: '-- Day and Time --', group: 'Day and Time', disabled: true },
      ...opts.filter(opt => opt.group === 'Day and Time'),

      { value: 'group', label: 'User Fields --', group: 'User Fields', disabled: true },
      ...opts.filter(opt => opt.group === 'User Fields'),

      { value: 'group', label: '-- Custom Fields --', group: 'Custom Fields', disabled: true },
      ...opts.filter(opt => opt.group === 'Custom Fields'),

      ...extraOptions,
    ]
  }

  const { type } = data;

  const equalityOptions = useMemo(() => getEqualityOptions(type, origin), [type, origin])
  const equalityOption = useMemo(() => equalityOptions.find(({ value }) => value === data.equality), [equalityOptions, data.equality])

  const renderEquality = () => {
    if (data.type === 'boolean' && !data.equality) {
      data.equality = "equal"
    }

    if (data.type === 'gender') {
      return null
    }

    return <RuleGroupItemWrapper>
      <EqualityGroup hint={equalityOption?.hint}>
        <Select
          name='equality-key'
          value={data.equality}
          options={equalityOptions}
          clearable={false}
          disabled={data.key === 'pid' && disabled ? false : disabled}
          onChange={opt => handleChangeEqualityInput(opt)} required />
      </EqualityGroup>
    </RuleGroupItemWrapper>
  }

  const renderInput = () => {
    if (['missing', 'not-missing'].includes(data.equality)) {
      return null
    }

    const customFieldOptions = data.key === 'usr_state' ? { options: stateOptions } : groupOpts.find(opt => opt.value === data.key);
    const isSelect = ['included', 'excluded', 'equal', 'not-equal', 'include-all'].includes(data.equality);
    const haveOptions = customFieldOptions && customFieldOptions.options && customFieldOptions.options.length

    if (isSelect && haveOptions) {
      return <SelectCustomFields
        data={data}
        customFieldsOptions={customFieldOptions}
        handleChangeValueInput={handleChangeValueInput}
        disabled={disabled}
      />
    }

    if (data.equality === 'validator') {
      return <RuleGroupItemWrapper>
        <RuleItemValidator
          value={data.value}
          onChange={value => handleChangeValueInput(value)}
          handleOpenValidatorModal={handleOpenValidatorModal}
          origin={origin}
          disabled={data.key === 'pid' && disabled ? false : disabled}
        />
      </RuleGroupItemWrapper>
    }

    if (['within-last', 'not-within-last'].includes(data.equality)) {
      return <RuleGroupItemWrapper>
        <RuleItemDate data={data} onChange={onChange}
          disabled={data.key === 'pid' && disabled ? false : disabled} />
      </RuleGroupItemWrapper>
    }

    if (type === 'domain') {
      return <RuleGroupItemWrapper>
        <DomainSelect
          value={data.value}
          onChange={handleChangeValueInput}
          equality={data.equality}
          disabled={disabled}
        />
      </RuleGroupItemWrapper>
    }

    if (type === 'dba') {
      return <RuleGroupItemWrapper>
        <ConsentDBAsSelect
          value={data.value}
          loadOptions={() => getConsentDBAsOptions().then(options => ({ options }))}
          onChange={handleChangeValueInput}
          equality={data.equality}
          disabled={disabled}
        />
      </RuleGroupItemWrapper>
    }

    return <RuleGroupItemWrapper>
      <RuleItemInput
        name={`${data.key}-key`}
        type={type}
        value={data.value}
        disabled={data.key === 'pid' && disabled ? false : disabled}
        onChange={value => handleChangeValueInput(value)}
      />
    </RuleGroupItemWrapper>
  }

  return <div className='flex gap-4 relative justify-center'>
    <div className='w-full'>
      <RuleGroupItemWrapper>
        <Select
          name={`${data.key}-option`}
          value={data.key}
          options={groupOpts}
          disabled={disabled}
          clearable={false}
          onChange={opt => handleChangeKeyInput(opt)} required />
      </RuleGroupItemWrapper>
    </div>
    {renderEquality()}
    {renderInput()}
    <ButtonIcon className='mt-2' content='Delete' onClick={onDeleteRow} disabled={disabled}>
      <Trash size={16} />
    </ButtonIcon>
  </div>
}

const EqualityGroup = ({ hint, children }) => {
  if (!hint) return <>{children}</>

  return <InputGroup>
    {children}
    <InputGroupText className='py-1 px-2' addonType="append">
      <InfoTip id='info-equality'>
        {hint}
      </InfoTip>
    </InputGroupText>
  </InputGroup>
}

const SelectCustomFields = ({ data, customFieldsOptions, handleChangeValueInput, disabled }) => {
  const valueInput = () => {
    if (!["start-with", "end-with", "contains"].includes(data.equality) && data.value && typeof data.value === 'string' && data.value.includes(',')) {
      return data.value.split(",").map(value => value)
    }

    return data.value;
  }

  const onChange = option => {
    if (Array.isArray(option)) {
      option = option.map(value => value.value).join(',');
      return handleChangeValueInput(option)
    }

    handleChangeValueInput(option && option.value || null)
  }

  const isMulti = ['included', 'excluded', 'include-all'].includes(data.equality);

  return <Select
    name="customField-key"
    value={valueInput()}
    options={customFieldsOptions && customFieldsOptions.options}
    key='customField'
    multi={isMulti}
    onChange={onChange}
    disabled={disabled}
  />
}

const DomainSelect = ({ value, onChange, equality, disabled }) => {
  const multi = ['included', 'excluded', 'include-all'].includes(equality)

  const getValue = () => {
    if (value && typeof value === 'string' && value.includes(',')) {
      const values = value.split(",")
      if (multi) {
        return values
      }
      onChange(values[0])
      return values[0]
    }
    return value
  }

  const setValue = option => {
    if (Array.isArray(option)) {
      return onChange(option.map(({ value }) => value).join(','))
    }
    return onChange(option && option.value || null)
  }

  return <Select
    async
    multi={multi}
    name="domain-key"
    value={getValue()}
    onChange={setValue}
    options={() => getDomainOptions().then(options => ({ options }))}
    disabled={disabled}
  />
}

const ConsentDBAsSelect = ({ value, loadOptions, onChange, equality, disabled }) => {
  const [loadedOptions, setLoadedOptions] = useState(null)

  const multi = ['included', 'excluded', 'include-all'].includes(equality)
  const key = loadedOptions?.options?.length || 0

  const handleLoadOptions = async () => {
    if (loadedOptions?.options?.length) {
      return loadedOptions
    }

    const updatedOptions = await loadOptions()

    if (multi) {
      const values = value.split(",")
      values.forEach(value => {
        const option = updatedOptions?.options?.find(option => option.value === value)
        if (value && !option) {
          const label = value.replace('static_', '').replaceAll('_', ' ')
          updatedOptions.options.push({ label, value })
        }
      })
    } else {
      const option = updatedOptions?.options?.find(option => option.value === value)
      if (value && !option) {
        const label = value.replace('static_', '').replaceAll('_', ' ')
        updatedOptions.options.push({ label, value })
      }
    }

    setLoadedOptions(updatedOptions)
    return updatedOptions
  }

  const handleValidate = ({ label }) => {
    if (!label || !typeof label === 'string' || !label.length) {
      return false
    }
    if (!/^[a-zA-Z0-9 _-]+$/.test(label)) {
      return false
    }
    return true
  }

  const handleCreate = ({ label }) => {
    label = label.trim()
    const value = `static_${snakeCase(lowerCase(label))}`
    return { label, value, isNew: true }
  }

  const getValue = () => {
    if (value && typeof value === 'string' && value.includes(',')) {
      const values = value.split(",")
      if (multi) {
        return values
      }
      onChange(values[0])
      return values[0]
    }
    return value
  }

  const setValue = option => {
    if (multi && Array.isArray(option)) {
      return onChange(option.map(({ value }) => value).join(','))
    }
    return onChange(option && option.value || null)
  }

  return <SelectTW
    async
    creatable
    name="consent-dba-key"
    key={key}
    multi={multi}
    required
    value={getValue()}
    onChange={setValue}
    loadOptions={handleLoadOptions}
    promptTextCreator={name => `Create: '${name}'`}
    newOptionCreator={handleCreate}
    isValidNewOption={handleValidate}
    disabled={disabled}
  />
}

RuleGroupItem.propTypes = {
  data: PropTypes.object,
  loadRuleKeys: PropTypes.func,
  onDeleteRow: PropTypes.func,
  origin: PropTypes.oneOf(['integration', 'page', 'path-page', 'profile', 'form-visibility', 'form-populate']),
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  handleValidatorModal: PropTypes.func,
}

export default RuleGroupItem;
